/**
 * GeneralStatusExplainer — Iter53 P50 Faza B UI
 *
 * Panel expandabil pe SaleDetail care afișează:
 *   - motivul, sursa, rule code, next action, versiunea, calculated_at
 *   - conflicte blocante (listă) și avertismente (listă)
 *   - snapshot al surselor decisive (internal, business, SF, supplier, QC, rework, withdrawn)
 *   - tab tehnic (raw JSON) doar pentru admin
 */
import { useState } from "react";
import { ChevronDown, ChevronUp, AlertOctagon, AlertTriangle, Info, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";

export const GeneralStatusExplainer = ({ sale, isAdmin = false }) => {
  // Auto-expand when there's an actionable signal: stale, blocking conflict, or warnings.
  // Operators need immediate visibility on these.
  const _autoExpand = !!(
    sale?.is_general_status_stale ||
    sale?.has_blocking_conflict ||
    (sale?.general_status_warnings && sale.general_status_warnings.length > 0)
  );
  const [expanded, setExpanded] = useState(_autoExpand);
  const [showJson, setShowJson] = useState(false);

  if (!sale?.general_status) return null;

  const conflicts = sale.general_status_conflicts || [];
  const warnings = sale.general_status_warnings || [];
  const c = sale.contract || {};
  const cs = c.status || {};

  return (
    <Card data-testid="general-status-explainer">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-center">
          <CardTitle className="text-base flex items-center gap-2">
            <Info className="w-4 h-4 text-teal-600" />
            De ce are acest status?
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)}
                  data-testid="explainer-toggle">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-4 text-sm" data-testid="explainer-body">
          {/* Row 1: reason + rule + source */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="text-xs text-slate-500 uppercase mb-1">Motiv</div>
              <div className="font-medium text-slate-800">{sale.general_status_reason || "—"}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500 uppercase mb-1">Regulă aplicată</div>
              <code className="text-xs bg-slate-100 px-2 py-1 rounded">{sale.general_status_rule_code || "—"}</code>
            </div>
            <div>
              <div className="text-xs text-slate-500 uppercase mb-1">Sursa decisivă</div>
              <div className="text-slate-800">{sale.general_status_source || "—"}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500 uppercase mb-1">Calculat</div>
              <div className="text-slate-800">
                {sale.general_status_calculated_at
                  ? new Date(sale.general_status_calculated_at).toLocaleString("ro-RO")
                  : "—"}
                {sale.general_status_calc_version && (
                  <span className="ml-2 text-xs text-slate-400">v{sale.general_status_calc_version}</span>
                )}
              </div>
            </div>
          </div>

          {/* Row 2: Sursele decisive (snapshot) */}
          <div>
            <div className="text-xs text-slate-500 uppercase mb-2">Surse folosite</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <div><span className="text-slate-500">Status intern:</span> <span className="text-slate-800">{cs.internal_status || "—"}</span></div>
              <div><span className="text-slate-500">Business status:</span> <span className="text-slate-800">{c.business_status || "—"}</span></div>
              <div><span className="text-slate-500">SF state:</span> <span className="text-slate-800">{cs.salesforce_contract_state || "—"}</span></div>
              <div><span className="text-slate-500">Supplier:</span> <span className="text-slate-800">{c.supplier_status || "—"}</span></div>
              <div><span className="text-slate-500">QC:</span> <span className="text-slate-800">{cs.quality_call_outcome || "—"}</span></div>
              <div><span className="text-slate-500">Rework activ:</span> <span className="text-slate-800">{sale.requires_rework ? "Da" : "Nu"}</span></div>
              <div><span className="text-slate-500">Withdrawn:</span> <span className="text-slate-800">{sale.is_withdrawn ? "Da" : "Nu"}</span></div>
              <div><span className="text-slate-500">Nr. contract:</span> <span className="text-slate-800">{c.contract_number || "—"}</span></div>
              <div><span className="text-slate-500">CSD:</span> <span className="text-slate-800">{c.csd_date || "—"}</span></div>
            </div>
          </div>

          {/* Faza B v2 — Stale indicator (source updated after calc) */}
          {sale.is_general_status_stale && (
            <div className="p-3 rounded border border-yellow-300 bg-yellow-50" data-testid="explainer-stale-block">
              <div className="text-xs uppercase mb-1 flex items-center gap-1 text-yellow-800 font-semibold">
                <RefreshCw className="w-4 h-4" /> Status posibil neactualizat
              </div>
              <div className="text-xs text-slate-800">
                Datele-sursă au fost modificate după ultima calculare a Statusului General.
                Următoarele câmpuri au timestamp mai recent decât <code>general_status_calculated_at</code>:
              </div>
              <ul className="mt-2 list-disc list-inside text-xs text-slate-700">
                {(sale.general_status_stale_sources || []).map((s) => (
                  <li key={s}><code>{s}</code></li>
                ))}
              </ul>
              <div className="text-[11px] text-slate-500 mt-2 italic">
                Statusul afișat este cel din backfill A1 și nu a fost recalculat la ultima modificare.
                Recalcularea automată este dezactivată în această fază.
              </div>
            </div>
          )}

          {/* Conflicts */}
          {conflicts.length > 0 && (
            <div>
              <div className="text-xs uppercase mb-2 flex items-center gap-1 text-rose-700">
                <AlertOctagon className="w-4 h-4" /> Conflicte blocante ({conflicts.length})
              </div>
              <ul className="space-y-1 pl-2">
                {conflicts.map((cf, i) => (
                  <li key={i} className="text-xs bg-rose-50 border-l-2 border-rose-400 p-2 rounded" data-testid={`conflict-item-${i}`}>
                    <div><code className="text-rose-800">{cf.code}</code></div>
                    <div className="text-slate-700 mt-1">{cf.message}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Warnings */}
          {warnings.length > 0 && (
            <div>
              <div className="text-xs uppercase mb-2 flex items-center gap-1 text-amber-700">
                <AlertTriangle className="w-4 h-4" /> Avertismente ({warnings.length})
              </div>
              <ul className="space-y-1 pl-2">
                {warnings.map((w, i) => (
                  <li key={i} className="text-xs bg-amber-50 border-l-2 border-amber-300 p-2 rounded" data-testid={`warning-item-${i}`}>
                    <div><code className="text-amber-800">{w.code}</code></div>
                    <div className="text-slate-700 mt-1">{w.message}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Admin-only raw JSON */}
          {isAdmin && (
            <div className="pt-3 border-t">
              <Button variant="outline" size="sm" onClick={() => setShowJson(!showJson)}
                      data-testid="explainer-json-toggle">
                {showJson ? "Ascunde" : "Afișează"} JSON tehnic (admin)
              </Button>
              {showJson && (
                <pre className="mt-2 bg-slate-900 text-slate-100 text-xs p-3 rounded overflow-auto max-h-80" data-testid="explainer-json-raw">
                  {JSON.stringify({
                    general_status: sale.general_status,
                    general_status_reason: sale.general_status_reason,
                    general_status_source: sale.general_status_source,
                    general_status_rule_code: sale.general_status_rule_code,
                    has_status_conflict: sale.has_status_conflict,
                    has_blocking_conflict: sale.has_blocking_conflict,
                    general_status_calc_version: sale.general_status_calc_version,
                    general_status_calculated_at: sale.general_status_calculated_at,
                    conflicts, warnings,
                  }, null, 2)}
                </pre>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default GeneralStatusExplainer;
